import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { getExhibitsData, type ExhibitIndex } from '../utils/exhibitsUtils';
import Header from './Header';
import Sidebar from './Sidebar';
import StatsModal from './StatsModal';
import CreditsScreen from './CreditsScreen';
import MuseumMapSVG from './MuseumMapSVG';

const MuseumTourContent: React.FC = () => {
    const [showCredits, setShowCredits] = useState(false);
    const [audioReady, setAudioReady] = useState(false);
    const [hasPlayedAfterCredits, setHasPlayedAfterCredits] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTrack, setCurrentTrack] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [tourMode, setTourMode] = useState<'guided' | 'free'>('free');
    const [visitedExhibits, setVisitedExhibits] = useState<Set<number>>(new Set());
    const [showStats, setShowStats] = useState(false);
    const [trackTimes, setTrackTimes] = useState<{ [key: number]: number }>({});
    const mobileSidebarRef = useRef<HTMLDivElement | null>(null);
    const desktopSidebarRef = useRef<HTMLDivElement | null>(null);
    const [bottomOverlayHeight, setBottomOverlayHeight] = useState(0);
    const [isNavigationPressed, setIsNavigationPressed] = useState(false);
    const resetUserInteractionRef = useRef<(() => void) | null>(null);

    // ---- This is key: get the ACTUAL, updated translation object! ----
    const { locale } = useLocale(); // make sure your context exposes locale (the translation object)
    // If not, use lang and pass to getLocale(lang): const { lang } = useLocale(); const locale = getLocale(lang);

    // Re-build exhibits array whenever the locale (or lang) changes!
    const exhibits = useMemo(() => getExhibitsData(locale), [locale]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);

    // Reset track if number of exhibits changes (e.g. language swap to a locale with fewer exhibits)
    useEffect(() => {
        if (currentTrack >= exhibits.length) setCurrentTrack(0);
    }, [exhibits.length, currentTrack]);

    // Mark the initial exhibit as visited when component first loads
    useEffect(() => {
        if (exhibits.length > 0) {
            setVisitedExhibits(prev => {
                const newSet = new Set(prev);
                newSet.add(exhibits[0].id);
                return newSet;
            });
        }
    }, [exhibits.length]);

    const goToTrack = (i: ExhibitIndex) => {
        // Save current time for previous track
        setTrackTimes(prev => ({
            ...prev,
            [currentTrack]: audioRef.current?.currentTime || 0
        }));

        // Mark the selected exhibit as visited
        setVisitedExhibits(prev => {
            const newSet = new Set(prev);
            newSet.add(exhibits[i].id);
            return newSet;
        });

        // If next exhibit has no audio, pause and reset audio
        if (!exhibits[i].audio && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.removeAttribute('src');
            setIsPlaying(false);
        }

        setCurrentTrack(i);
        setIsPlaying(false);
    };

    const prevTrack = () => {
        if (currentTrack > 0) {
            setIsNavigationPressed(true);
            goToTrack(currentTrack - 1);
            // Reset user interaction to enable autocenter
            if (resetUserInteractionRef.current) {
                resetUserInteractionRef.current();
            }
        }
    };

    const nextTrack = () => {
        if (currentTrack < exhibits.length - 1) {
            setIsNavigationPressed(true);
            goToTrack(currentTrack + 1);
            // Reset user interaction to enable autocenter
            if (resetUserInteractionRef.current) {
                resetUserInteractionRef.current();
            }
        }
    };

    const toggleVisited = useCallback(() => {
        const currentExhibit = exhibits[currentTrack];
        setVisitedExhibits(prev => {
            const newSet = new Set(prev);
            if (newSet.has(currentExhibit.id)) {
                newSet.delete(currentExhibit.id);
            } else {
                newSet.add(currentExhibit.id);
            }
            return newSet;
        });
    }, [currentTrack, exhibits]);

    const formatTime = (time: number) =>
        `${Math.floor(time / 60)}:${Math.floor(time % 60).toString().padStart(2, '0')}`;

    // Setup AudioContext and GainNode
    useEffect(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        if (!gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.connect(audioContextRef.current.destination);
            gainNodeRef.current.gain.value = volume;
        }
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            setAudioReady(true);
            const savedTime = trackTimes[currentTrack] || 0;
            audio.currentTime = savedTime;
            setDuration(audio.duration);
            setCurrentTime(savedTime);
        };
        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };
        const handleEnded = () => {
            setIsPlaying(false);
            if (tourMode === 'guided' && currentTrack < exhibits.length - 1) {
                setTimeout(() => {
                    nextTrack();
                }, 1000);
            }
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);

        // Manually trigger the metadata handler if audio is already loaded
        if (audio.readyState >= 1) {
            handleLoadedMetadata();
        }

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [currentTrack, exhibits, tourMode, trackTimes]);

    // Sync volume to gain node and HTMLAudioElement
    useEffect(() => {
        if (gainNodeRef.current) gainNodeRef.current.gain.value = volume;
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    // Measure bottom overlay height (mobile/desktop) and update on resize
    useEffect(() => {
        const updateOverlayHeight = () => {
            const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
            const el = isDesktop ? desktopSidebarRef.current : mobileSidebarRef.current;
            const h = el ? Math.floor(el.getBoundingClientRect().height) : 0;
            setBottomOverlayHeight(h);
        };

        const ro = new ResizeObserver(updateOverlayHeight);
        if (mobileSidebarRef.current) ro.observe(mobileSidebarRef.current);
        if (desktopSidebarRef.current) ro.observe(desktopSidebarRef.current);
        window.addEventListener('resize', updateOverlayHeight);
        updateOverlayHeight();
        return () => {
            window.removeEventListener('resize', updateOverlayHeight);
            ro.disconnect();
        };
    }, []);

    const togglePlayPause = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (!exhibits[currentTrack].audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            setTrackTimes(prev => ({
                ...prev,
                [currentTrack]: audio.currentTime
            }));
        } else {
            if (audioContextRef.current?.state === 'suspended') {
                audioContextRef.current.resume();
            }
            audio.play().then(() => {
                setIsPlaying(true);
                setVisitedExhibits(prev => new Set(prev).add(exhibits[currentTrack].id));
            }).catch((e: any) => {
                // Likely due to autoplay restriction
                console.error("Play error:", e);
            });
        }
    }, [isPlaying, currentTrack, exhibits]);

    // Auto-play after credits if desired
    useEffect(() => {
        if (!showCredits && tourMode === 'guided' && audioReady && !hasPlayedAfterCredits) {
            // Uncomment to autoplay after credits:
            // togglePlayPause();
            setHasPlayedAfterCredits(true);
        }
    }, [showCredits, tourMode, audioReady, hasPlayedAfterCredits]);

    return (
        <div className="min-h-screen mobile-vh text-white overflow-hidden overscroll-none" style={{ background: 'var(--gradient-primary)' }}>
            <audio ref={audioRef} preload="auto" src={exhibits[currentTrack]?.audio ?? undefined} />
            {showCredits ? <CreditsScreen onClose={() => setShowCredits(false)} /> : (
                <>
                    <Header
                        totalVisited={visitedExhibits.size}
                        totalExhibits={exhibits.length}
                        onShowStats={() => setShowStats(true)}
                        onShowCredits={() => setShowCredits(true)}
                    />
                    <div className="flex flex-col h-[calc(100vh-80px)] lg:h-[calc(100vh-80px)] mobile-vh overscroll-none">
                        {/* Map - Full width with proper mobile spacing */}
                        <div className="flex-1 min-h-0 lg:min-h-full lg:pb-20 pb-32"> 
                            <MuseumMapSVG
                                exhibits={exhibits}
                                currentTrack={currentTrack}
                                visitedExhibits={visitedExhibits}
                                selectExhibit={goToTrack}
                                selectedExhibit={exhibits[currentTrack]}
                                bottomOverlayHeight={bottomOverlayHeight}
                                isNavigationPressed={isNavigationPressed}
                                setIsNavigationPressed={setIsNavigationPressed}
                                resetUserInteraction={resetUserInteractionRef}
                            />
                        </div>
                    </div>
                    {/* Mobile Sidebar - Fixed at bottom for mobile */}
                    <div ref={mobileSidebarRef} className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
                        <Sidebar
                            tourMode={tourMode}
                            setTourMode={setTourMode}
                            currentExhibit={exhibits[currentTrack]}
                            currentTrack={currentTrack}
                            exhibits={exhibits}
                            visitedExhibits={visitedExhibits}
                            selectExhibit={goToTrack}
                            isPlaying={isPlaying}
                            togglePlayPause={togglePlayPause}
                            toggleVisited={toggleVisited}
                            prevTrack={prevTrack}
                            nextTrack={nextTrack}
                            audioRef={audioRef}
                            formatTime={formatTime}
                            currentTime={currentTime}
                            duration={duration}
                            progress={duration > 0 ? (currentTime / duration) * 100 : 0}
                            volume={volume}
                            setVolume={setVolume}
                            audioKey={exhibits[currentTrack].audio}
                            containerRef={mobileSidebarRef}
                        />
                    </div>
                    {/* Desktop Sidebar - Fixed at bottom */}
                    <div ref={desktopSidebarRef} className="hidden lg:block">
                        <Sidebar
                            tourMode={tourMode}
                            setTourMode={setTourMode}
                            currentExhibit={exhibits[currentTrack]}
                            currentTrack={currentTrack}
                            exhibits={exhibits}
                            visitedExhibits={visitedExhibits}
                            selectExhibit={goToTrack}
                            isPlaying={isPlaying}
                            togglePlayPause={togglePlayPause}
                            toggleVisited={toggleVisited}
                            prevTrack={prevTrack}
                            nextTrack={nextTrack}
                            audioRef={audioRef}
                            formatTime={formatTime}
                            currentTime={currentTime}
                            duration={duration}
                            progress={duration > 0 ? (currentTime / duration) * 100 : 0}
                            volume={volume}
                            setVolume={setVolume}
                            audioKey={exhibits[currentTrack].audio}
                            containerRef={desktopSidebarRef}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default MuseumTourContent;